import { createRouter, createWebHistory } from 'vue-router';
import { trackNavigation, trackPreloadHit, getPerformanceReport } from '../utils/performance.js';
import { resolveSsoReturn } from '@/utils/ssoReturn.mjs';
import { FINANCE_ONLY, resolveFinanceConfinement, storedAccessScope } from '@/utils/postLogin.mjs';

// ===================================================================
// 🏆 CODE SPLITTING STRATEGY - CHAMPIONSHIP LEVEL PERFORMANCE
// ===================================================================
// 
// LAZY LOADING WITH STRATEGIC CHUNKING:
// - Authentication chunk: login, register, blocked (small, rarely used together)
// - Core app chunks: main views loaded on demand
// - Admin chunk: isolated admin functionality
// - Volta chunk: FIFA-specific features grouped together
// 
// CHUNK NAMING CONVENTION:
// - auth-* : Authentication related components  
// - core-* : Main application features
// - admin-* : Administrative functionality
// - volta-* : FIFA Volta specific features
// - legacy-* : Legacy components being phased out
// ===================================================================

// ===================================================================
// AUTHENTICATION CHUNK - Loaded only when needed
// ===================================================================
const Login = () => import(
  /* webpackChunkName: "auth-login" */ 
  '../views/Login.vue'
);
const Register = () => import(
  /* webpackChunkName: "auth-register" */ 
  '../views/Register.vue'
);
const Blocked = () => import(
  /* webpackChunkName: "auth-blocked" */
  '../views/Blocked.vue'
);
const Maintenance = () => import(
  /* webpackChunkName: "auth-maintenance" */
  '../views/Maintenance.vue'
);
const TwoFactorVerify = () => import(
  /* webpackChunkName: "auth-2fa-verify" */
  '../views/TwoFactorVerify.vue'
);
const LinkDiscord = () => import(
  /* webpackChunkName: "auth-link-discord" */
  '../views/LinkDiscord.vue'
);
const DeviceManagement = () => import(
  /* webpackChunkName: "auth-devices" */
  '../views/DeviceManagement.vue'
);
const DesktopAuthorize = () => import(
  /* webpackChunkName: "auth-desktop-authorize" */
  '../views/DesktopAuthorize.vue'
);

// ===================================================================
// REDESIGNED COMPONENTS CHUNK - Main UI
// ===================================================================
const MyBetsRedesigned = () => import(
  /* webpackChunkName: "v2-bets" */
  '../views/MyBetsRedesigned.vue'
);
const AccountDashboardV2 = () => import(
  /* webpackChunkName: "v2-account-advanced" */ 
  '../views/AccountDashboardV2.vue'
);
const PartidasV2 = () => import(
  /* webpackChunkName: "v2-matches" */ 
  '../views/PartidasV2.vue'
);

// ===================================================================
// LIVE DASHBOARD CHUNK - Real-time features
// ===================================================================
const LiveDashboard = () => import(
  /* webpackChunkName: "live-dashboard" */
  '../views/LiveDashboard.vue'
);

const LiveEmbedPanel = () => import(
  /* webpackChunkName: "live-embed-panel" */
  '../views/LiveEmbedPanel.vue'
);

// ===================================================================
// ACCOUNT OPS CHUNK - Automated account operations
// ===================================================================
const AccountOpsDashboard = () => import(
  /* webpackChunkName: "account-ops" */
  '../views/AccountOpsDashboard.vue'
);
const AccountOpsDetail = () => import(
  /* webpackChunkName: "account-ops" */
  '../views/AccountOpsDetail.vue'
);
const AccountOpsClose = () => import(
  /* webpackChunkName: "account-ops" */
  '../views/AccountOpsClose.vue'
);
const AccountOpsSetup = () => import(
  /* webpackChunkName: "account-ops" */
  '../views/AccountOpsSetup.vue'
);

// ===================================================================
// ADMIN CHUNK - Administrative functionality (isolated)
// ===================================================================
const AdminDashboard = () => import(
  /* webpackChunkName: "admin-dashboard" */ 
  '../views/AdminDashboard.vue'
);

// ===================================================================
// FIFA VOLTA CHUNK - Game-specific features grouped together
// ===================================================================
const LiveMatchesVolta = () => import(
  /* webpackChunkName: "volta-live" */ 
  '../views/LiveMatchesVolta.vue'
);
const MatchHistoryVolta = () => import(
  /* webpackChunkName: "volta-history" */ 
  '../views/MatchHistoryVolta.vue'
);
const LiveDashboardVolta = () => import(
  /* webpackChunkName: "volta-dashboard" */ 
  '../views/LiveDashboardVolta.vue'
);

// ===================================================================
// AUTHENTICATION & AUTHORIZATION LOGIC
// ===================================================================

// Função simulada para verificar se o usuário está logado
function isLoggedIn() {
  // Substitua essa lógica pela verificação real do estado de login do seu usuário
  return localStorage.getItem('userLoggedIn') === 'true';
}

// Função para verificar se o usuário é o admin 'stabem'
function isStabemUser() {
  return localStorage.getItem('username') === 'stabem';
}

// Função para verificar se o IP do usuário está bloqueado
function isBlockedCookie() {
  return document.cookie.split(';').some(function (cookie) {
    return cookie.trim().startsWith('dale_blocked=1');
  });
}
function isIpBlocked() {
  return localStorage.getItem('ipBlocked') === 'true' || isBlockedCookie();
}

// Função para verificar se o usuário está em modo de manutenção
function isMaintenanceUser() {
  return localStorage.getItem('userMaintenance') === 'true';
}

// Função para limpar todos os tokens de autorização
function clearAllAuthTokens() {
  localStorage.removeItem('userToken');
  localStorage.removeItem('userLoggedIn');
  localStorage.removeItem('username');
  localStorage.removeItem('botPermissions');

  // Limpar todos os cookies também
  document.cookie.split(';').forEach((cookie) => {
    const [name] = cookie.trim().split('=');
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
}

// ===================================================================
// ROUTER CONFIGURATION WITH SMART PRELOADING
// ===================================================================
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [
    // ===============================================================
    // PUBLIC ROUTES - Authentication chunk (minimal initial load)
    // ===============================================================
    {
      path: '/blocked',
      component: Blocked,
      meta: {
        allowWhenBlocked: true,
        preload: false // Never preload blocked page
      },
    },
    // Embedded compact live-matches panel for app.dale365.com (WorkAdventure)
    {
      path: '/embed/live-panel',
      component: LiveEmbedPanel,
      meta: {
        layout: 'blank',
        allowAnonymous: true,
        preload: false,
      },
    },
    {
      path: '/maintenance',
      component: Maintenance,
      meta: {
        allowInMaintenance: true,
        preload: false // Never preload maintenance page
      },
    },
    {
      path: '/login',
      component: Login,
      meta: {
        preload: ['v2-matches'] // Preload likely next route after login
      },
    },
    {
      path: '/register',
      component: Register,
      meta: {
        preload: ['auth-login'] // Preload login as likely next step
      },
    },
    {
      path: '/2fa/verify',
      component: TwoFactorVerify,
    },
    {
      path: '/2fa/link-discord',
      component: LinkDiscord,
    },
    {
      path: '/profile/devices',
      component: DeviceManagement,
      meta: { requiresAuth: true },
    },
    {
      path: '/desktop/authorize',
      component: DesktopAuthorize,
      meta: { requiresAuth: true },
    },

    // ===============================================================
    // MAIN APP ROUTES - Core functionality with smart preloading
    // ===============================================================
    {
      path: '/',
      redirect: '/partidas-v2',
      meta: { requiresAuth: true },
    },
    
    // LEGACY ROUTES - Redirect to V2
    {
      path: '/partidas',
      redirect: '/partidas-v2',
    },
    {
      path: '/ao-vivo',
      redirect: '/ao-vivo-v2',
    },
    {
      path: '/apostas',
      redirect: '/apostas-v2',
    },
    {
      path: '/conta',
      redirect: '/conta-v2',
    },
    
    // V2 ROUTES - Primary application features with strategic preloading
    {
      path: '/partidas-v2',
      component: PartidasV2,
      meta: { 
        requiresAuth: true,
        preload: ['v2-bets', 'live-dashboard'], // Most likely next destinations
        priority: 'high' // Mark as high priority route
      },
    },
    {
      path: '/apostas-v2',
      component: MyBetsRedesigned,
      meta: { 
        requiresAuth: true,
        preload: ['v2-matches'] // Return to matches after checking bets
      },
    },
    {
      path: '/conta-v2',
      component: AccountDashboardV2,
      meta: { 
        requiresAuth: true,
        preload: ['v2-matches'] // Return to main app after account
      },
    },
    
    // LIVE FEATURES - High-performance real-time components  
    {
      path: '/ao-vivo-v2',
      component: LiveDashboard,
      meta: { 
        requiresAuth: true,
        preload: ['v2-bets'] // Check bets after watching live
      },
    },
    {
      path: '/live-dashboard',
      redirect: '/ao-vivo-v2',
    },

    // ACCOUNT OPS - Spreadsheet replacement for account automation
    {
      path: '/account-ops',
      component: AccountOpsDashboard,
      meta: {
        requiresAuth: true,
        preload: false
      },
    },
    {
      path: '/account-ops/setup',
      component: AccountOpsSetup,
      meta: {
        requiresAuth: true,
        preload: false
      },
    },
    {
      path: '/account-ops/:id/close',
      component: AccountOpsClose,
      meta: {
        requiresAuth: true,
        preload: false
      },
    },
    {
      path: '/account-ops/:id',
      component: AccountOpsDetail,
      meta: {
        requiresAuth: true,
        preload: false
      },
    },

    // ADMIN ROUTES - Isolated admin functionality
    {
      path: '/admin',
      component: AdminDashboard,
      meta: { 
        requiresAuth: true, 
        requiresAdmin: true,
        preload: false // Admin doesn't need preloading
      },
    },
    
    // ===============================================================
    // FIFA VOLTA ROUTES - Game-specific feature set
    // ===============================================================
    {
      path: '/volta-dashboard',
      component: LiveDashboardVolta,
      meta: { 
        requiresAuth: true,
        preload: ['volta-live', 'volta-history'] // Related Volta features
      },
    },
    {
      path: '/ao-vivo-volta',
      component: LiveMatchesVolta,
      meta: { 
        requiresAuth: true,
        preload: ['volta-dashboard'] // Dashboard after live matches
      },
    },
    {
      path: '/partidas-volta',
      component: MatchHistoryVolta,
      meta: { 
        requiresAuth: true,
        preload: ['volta-live'] // Check live after history
      },
    },
    
    // FALLBACK - Smart redirect based on authentication
    {
      path: '/:pathMatch(.*)*',
      redirect: () => {
        // Funciona como uma função que decide para onde redirecionar
        return isLoggedIn() ? '/partidas-v2' : '/login';
      },
    },
  ],
});

// ===================================================================
// SMART ROUTE PRELOADING SYSTEM
// ===================================================================
let preloadedChunks = new Set();

function preloadRouteChunks(chunkNames) {
  if (!chunkNames || !Array.isArray(chunkNames)) return;
  
  chunkNames.forEach(chunkName => {
    if (preloadedChunks.has(chunkName)) return; // Already preloaded
    
    // Use requestIdleCallback for non-blocking preload
    const preloadChunk = () => {
      // Create link element for preloading
      const link = document.createElement('link');
      link.rel = 'prefetch'; // Use prefetch instead of preload for lower priority
      link.as = 'script';
      link.href = `js/${chunkName}.js`;
      
      // Add error handling
      link.onerror = () => {
        console.warn(`Failed to prefetch chunk: ${chunkName}`);
        preloadedChunks.delete(chunkName); // Allow retry
      };
      
      link.onload = () => {
        console.log(`✅ Prefetched chunk: ${chunkName}`);
      };
      
      // Add to head and track
      document.head.appendChild(link);
      preloadedChunks.add(chunkName);
    };
    
    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preloadChunk, { timeout: 5000 });
    } else {
      setTimeout(preloadChunk, 100);
    }
  });
}

// ===================================================================
// NAVIGATION GUARDS WITH PERFORMANCE OPTIMIZATIONS
// ===================================================================

// Performance tracking
let navigationStartTime = 0;

// Interceptar todas as navegações antes que ocorram
router.beforeEach((to, from, next) => {
  // Start performance tracking
  navigationStartTime = performance.now();

  // IMPORTANTE: Se já estiver indo para /blocked, permitir sempre sem verificações
  if (to.path === '/blocked') {
    console.log('Navegando para /blocked - permitindo sem verificações');
    next();
    return;
  }

  // Verificar se o usuário está em modo de manutenção
  if (isMaintenanceUser()) {
    // Se já está indo para /maintenance, permitir
    if (to.path === '/maintenance') {
      console.log('Navegando para /maintenance - usuário em manutenção');
      next();
      return;
    }

    // Se está em manutenção mas tentando acessar outra página, bloquear
    console.warn('Usuário em manutenção tentando acessar:', to.path, '- redirecionando para /maintenance');
    next('/maintenance');
    return;
  }

  // Verificar o bloqueio para outras rotas
  if (isIpBlocked()) {
    console.warn('Tentativa de navegação bloqueada:', to.path);

    // Limpar tokens de autenticação
    clearAllAuthTokens();

    // Redirecionar para a página de bloqueio usando o router
    // NÃO usar window.location.href que causa loops
    next('/blocked');
    return;
  }

  // Usuario finance_only (#333): so existe para o Financeiro. Fora de /login e
  // /2fa/* (o /blocked ja saiu la em cima) ele vai para la, com o cookie de SSO
  // regravado. Conveniencia apenas: quem recusa o token dele e o backend.
  if (isLoggedIn() && storedAccessScope(localStorage) === FINANCE_ONLY) {
    const confinement = resolveFinanceConfinement({
      path: to.path,
      rawNext: to.query.next,
      token: localStorage.getItem('userToken'),
      location: window.location,
      storage: sessionStorage,
    });
    if (confinement.action === 'redirect') {
      document.cookie = confinement.cookie;
      window.location.assign(confinement.url);
      next(false);
      return;
    }
    if (confinement.action === 'login') {
      // Anti-loop disparou: para no /login, nunca numa tela da plataforma.
      next({ path: '/login', query: { acesso: 'financeiro' } });
      return;
    }
    next();
    return;
  }

  // Verifica se a rota requer autenticação e o usuário não está logado
  if (to.meta.requiresAuth && !isLoggedIn()) {
    next({ path: '/login', query: { next: to.fullPath } });
  } else if (to.meta.requiresAdmin && !isStabemUser()) {
    // Se a rota requer admin e o usuário não é 'stabem', redireciona
    console.warn('Acesso negado ao painel administrativo');
    next({ path: '/partidas-v2' });
  } else if (!to.meta.requiresAuth && isLoggedIn() && to.path === '/login') {
    // Ja logado e veio de um subdominio (?next=https://<x>.dale365.com/...):
    // regrava o cookie de SSO e devolve para la em vez de prender em /partidas-v2.
    const ssoReturn = resolveSsoReturn({
      rawNext: to.query.next,
      token: localStorage.getItem('userToken'),
      location: window.location,
      storage: sessionStorage,
    });
    if (ssoReturn) {
      document.cookie = ssoReturn.cookie;
      window.location.assign(ssoReturn.url);
      next(false);
      return;
    }
    next({ path: '/partidas-v2' }); // Se o usuário está logado e tenta acessar /login, redireciona para /partidas
  } else {
    next(); // Caso contrário, continua a navegação normalmente
  }
});

// After navigation completes, preload likely next routes
router.afterEach((to, from) => {
  // Track navigation performance
  if (navigationStartTime > 0) {
    const navigationTime = performance.now() - navigationStartTime;
    trackNavigation(from.path, to.path, navigationTime);
    navigationStartTime = 0; // Reset for next navigation
  }
  // Preload chunks for likely next routes based on current route
  if (to.meta.preload) {
    // Wait for the current route to fully load before preloading
    setTimeout(() => {
      preloadRouteChunks(to.meta.preload);
    }, 2000); // Increased to 2 seconds for better UX
  }
  
  // Preload vendor chunks on first visit if not already loaded
  if (!preloadedChunks.has('vendor-vue') && performance.now() > 3000) {
    preloadRouteChunks(['vendor-ui', 'vendor-utils']);
  }
  
  // Track route changes for analytics
  console.log(`🎯 Route loaded: ${to.path} | Chunk: ${to.meta.preload || 'none'}`);
  
  // Show performance report in development occasionally
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
    setTimeout(() => {
      console.log('📊 Performance Report:');
      getPerformanceReport();
    }, 3000);
  }
});

export default router;
